/**
 * Event and signal search.
 *
 * A search screen, not a query language. The platform's cost model means the interesting fields
 * were chosen at onboarding, so the useful filters are the small set that always exist — class,
 * severity, source, and free text across the message and entities.
 *
 * The panel that matters most is the one showing what an event actually stored versus what its
 * source emitted, because that is where an operator discovers a field policy is wrong.
 */

import { useState } from 'react'
import {
  api,
  type BastionEvent,
  type SavedQuery,
  type Signal,
  type SourceSummary,
} from '../api'
import { useSession } from '../App'
import {
  Async,
  Card,
  Chip,
  Empty,
  Modal,
  Mono,
  ObservableList,
  SeverityChip,
  Note,
  fmtTime,
  useAsync,
  usePoll,
  useToast,
} from '../components/ui'

export default function Explore() {
  const { me } = useSession()
  const [tab, setTab] = useState<'events' | 'signals'>('events')
  const [q, setQ] = useState('')
  const [severity, setSeverity] = useState('')
  const [source, setSource] = useState('')
  const [live, setLive] = useState(true)
  const [inspecting, setInspecting] = useState<BastionEvent | null>(null)

  const qs = `limit=200${q ? `&q=${encodeURIComponent(q)}` : ''}${severity ? `&severity=${severity}` : ''}${source ? `&source=${encodeURIComponent(source)}` : ''}`
  const events = useAsync<BastionEvent[]>(() => api.get<BastionEvent[]>(`/events?${qs}`), [me.tenant, qs])
  const signals = useAsync<Signal[]>(() => api.get<Signal[]>(`/signals?${qs}`), [me.tenant, qs])
  const sources = useAsync<SourceSummary[]>(() => api.get<SourceSummary[]>('/sources'), [me.tenant])
  const saved = useAsync<SavedQuery[]>(() => api.get<SavedQuery[]>('/queries'), [me.tenant])

  /** Load a saved search back into the filter bar. */
  function apply(query: SavedQuery) {
    if (query.target === 'events' || query.target === 'signals') setTab(query.target)
    setQ(query.filter.text ?? '')
    setSeverity(query.filter.severity ?? '')
    setSource(query.filter.source ?? '')
  }

  usePoll(() => (tab === 'events' ? events.reload() : signals.reload()), 8000, live)

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Explore</h1>
          <div className="sub">
            The retained hot window. Events older than a source's retention have been rolled up;
            what is searchable here is what the field policies decided to keep.
          </div>
        </div>
        <div className="actions">
          <button className={tab === 'events' ? 'primary' : ''} onClick={() => setTab('events')}>
            Events
          </button>
          <button className={tab === 'signals' ? 'primary' : ''} onClick={() => setTab('signals')}>
            Signals
          </button>
        </div>
      </div>

      <SavedSearches saved={saved} onApply={apply} />

      <Card
        actions={
          <>
            <input
              placeholder="Search message and entities…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              style={{ width: 260 }}
              aria-label="Search"
            />
            <select value={severity} onChange={(e) => setSeverity(e.target.value)} style={{ width: 'auto' }} aria-label="Severity">
              <option value="">All severities</option>
              {['critical', 'high', 'medium', 'low', 'info'].map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            {tab === 'events' && (
              <select value={source} onChange={(e) => setSource(e.target.value)} style={{ width: 'auto' }} aria-label="Source">
                <option value="">All sources</option>
                {(sources.data ?? []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.display_name}
                  </option>
                ))}
              </select>
            )}
            <label className="row small muted" style={{ margin: 0, gap: 5 }}>
              <input type="checkbox" checked={live} onChange={(e) => setLive(e.target.checked)} />
              Live
            </label>
            <SaveSearch
              target={tab}
              filter={{ text: q, severity: severity || null, source: tab === 'events' ? source : '' }}
              saved={saved}
            />
          </>
        }
        title={tab === 'events' ? 'Events' : 'Signals'}
        tight
      >
        {tab === 'events' ? (
          <Async
            state={events}
            empty={
              <Empty title="No matching events">
                Either nothing has been ingested yet, or the filters exclude everything retained.
              </Empty>
            }
          >
            {(list) => (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Severity</th>
                      <th>Class</th>
                      <th>Source</th>
                      <th>Message</th>
                      <th>Entities</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((e) => (
                      <tr key={e.id} className="clickable" onClick={() => setInspecting(e)}>
                        <td className="small muted nowrap">{fmtTime(e.timestamp)}</td>
                        <td><SeverityChip severity={e.severity} /></td>
                        <td><Chip tone="outline">{e.class}</Chip></td>
                        <td className="small">{e.source}</td>
                        <td className="small">
                          <div className="truncate" title={e.message} style={{ maxWidth: '46ch' }}>
                            {e.message}
                          </div>
                          {e.fields['intel.matched'] === true && (
                            <Chip tone="critical" title="An observable matched threat intelligence">
                              intel hit
                            </Chip>
                          )}
                        </td>
                        <td><ObservableList observables={e.observables} max={4} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Async>
        ) : (
          <Async state={signals} empty={<Empty title="No signals" />}>
            {(list) => (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Severity</th>
                      <th>Signal</th>
                      <th>Kind</th>
                      <th>Technique</th>
                      <th>Evidence</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((s) => (
                      <tr key={s.id}>
                        <td className="small muted nowrap">{fmtTime(s.timestamp)}</td>
                        <td><SeverityChip severity={s.severity} /></td>
                        <td>
                          <div style={{ fontWeight: 550 }}>{s.title}</div>
                          <div className="faint small">{s.rationale}</div>
                          <ObservableList observables={s.observables} max={4} />
                        </td>
                        <td><Chip tone="outline">{s.kind.replace(/_/g, ' ')}</Chip></td>
                        <td className="small">
                          {s.techniques.map((t) => (
                            <Chip key={t.id} tone="outline" title={t.name}>
                              {t.id}
                            </Chip>
                          ))}
                        </td>
                        <td className="small tnum">
                          {s.evidence.length}
                          {s.evidence_count > s.evidence.length && (
                            <span className="faint" title="The signal cites a sample of a larger set">
                              {' '}of {s.evidence_count}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Async>
        )}
      </Card>

      {inspecting && <EventDetail event={inspecting} onClose={() => setInspecting(null)} />}
    </>
  )
}

/**
 * The searches this workspace has kept.
 *
 * Shared within the tenant rather than per person: a saved search is the SOC's working knowledge,
 * and a private one has to be rediscovered by whoever is on shift next.
 */
function SavedSearches({
  saved,
  onApply,
}: {
  saved: ReturnType<typeof useAsync<SavedQuery[]>>
  onApply: (q: SavedQuery) => void
}) {
  const { can } = useSession()
  const toast = useToast()
  const list = saved.data ?? []
  if (list.length === 0) return null
  return (
    <Card title="Saved searches" subtitle="Kept by this workspace" tight>
      <div className="row wrap" style={{ gap: 6 }}>
        {list.map((s) => (
          <span key={s.id} className="row" style={{ gap: 2 }}>
            <button className="sm" onClick={() => onApply(s)} title={s.description || s.name}>
              {s.name} <span className="faint">· {s.target}</span>
            </button>
            {can('write_incidents') && (
              <button
                className="sm"
                aria-label={`Delete ${s.name}`}
                onClick={() => {
                  api
                    .del(`/queries/${s.id}`)
                    .then(() => {
                      toast('ok', `Deleted "${s.name}".`)
                      saved.reload()
                    })
                    // A query a dashboard is using is refused, and the message names the
                    // dashboard: an orphaned card renders empty with no explanation.
                    .catch((e: Error) => toast('error', e.message))
                }}
              >
                ×
              </button>
            )}
          </span>
        ))}
      </div>
    </Card>
  )
}

/** Keep the current filter bar as a named search. */
function SaveSearch({
  target,
  filter,
  saved,
}: {
  target: 'events' | 'signals'
  filter: { text: string; severity: string | null; source: string }
  saved: ReturnType<typeof useAsync<SavedQuery[]>>
}) {
  const { can } = useSession()
  const toast = useToast()
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  if (!can('write_incidents')) return null

  const empty = !filter.text.trim() && !filter.severity && !filter.source

  return (
    <>
      <button className="sm" onClick={() => setOpen(true)} disabled={empty} title={empty ? 'Set at least one filter first' : 'Keep this search'}>
        Save search
      </button>
      {open && (
        <Modal
          title="Save this search"
          onClose={() => setOpen(false)}
          footer={
            <>
              <button onClick={() => setOpen(false)}>Cancel</button>
              <button
                className="primary"
                disabled={busy || !name.trim()}
                onClick={() => {
                  setBusy(true)
                  api
                    .post<SavedQuery>('/queries', { name, target, filter })
                    .then(() => {
                      toast('ok', `Saved "${name}".`)
                      setOpen(false)
                      setName('')
                      saved.reload()
                    })
                    .catch((e: Error) => toast('error', e.message))
                    .finally(() => setBusy(false))
                }}
              >
                {busy ? 'Saving…' : 'Save'}
              </button>
            </>
          }
        >
          <div className="field">
            <label htmlFor="query-name">Name</label>
            <input
              id="query-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Denied outbound from finance"
            />
          </div>
          <Note>
            A saved search is a search, not a snapshot: running it later returns what matches then.
            It is shared with everyone in this workspace, and it can be put on a dashboard.
          </Note>
        </Modal>
      )}
    </>
  )
}

function EventDetail({ event, onClose }: { event: BastionEvent; onClose: () => void }) {
  const skew = event.received_at - event.timestamp
  return (
    <Modal title="Event" onClose={onClose} wide footer={<button onClick={onClose}>Close</button>}>
      <dl className="kv">
        <dt>Id</dt>
        <dd><Mono copy>{event.id}</Mono></dd>
        <dt>Event time</dt>
        <dd>{fmtTime(event.timestamp)}</dd>
        <dt>Received</dt>
        <dd>
          {fmtTime(event.received_at)}
          {Math.abs(skew) > 60000 && (
            <>
              {' '}
              <Chip tone="warn" title="The source's clock differs from receipt time by more than a minute. Correlation windows use the source time, so a large skew stops this source chaining with others.">
                clock skew {Math.round(skew / 1000)}s
              </Chip>
            </>
          )}
        </dd>
        <dt>Source</dt>
        <dd>
          {event.source} <span className="faint small">parsed by {event.source_type}</span>
        </dd>
        <dt>Class</dt>
        <dd><Chip tone="outline">{event.class}</Chip></dd>
        <dt>Severity</dt>
        <dd><SeverityChip severity={event.severity} /></dd>
        <dt>Message</dt>
        <dd>{event.message}</dd>
      </dl>

      <h3 style={{ margin: '16px 0 6px' }}>Entities</h3>
      <ObservableList observables={event.observables} max={40} />

      <h3 style={{ margin: '16px 0 6px' }}>Stored fields</h3>
      <div className="table-wrap">
        <table>
          <tbody>
            {Object.entries(event.fields).map(([k, v]) => (
              <tr key={k}>
                <td className="mono small" style={{ width: '30%' }}>
                  {k}
                </td>
                <td className="mono small" style={{ wordBreak: 'break-word' }}>
                  {String(v)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3 style={{ margin: '16px 0 6px' }}>Raw line</h3>
      {event.raw ? (
        <pre className="mono" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: 0 }}>
          {event.raw}
        </pre>
      ) : (
        <p className="small muted">
          This source's retention policy does not keep raw lines. That is usually the right call —
          it is the single largest storage cost — but it means the original text is not available
          for this event.
        </p>
      )}
    </Modal>
  )
}
